/**
 * Helpers for JasperReports Server report options (saved input control presets).
 */

const normalizeResourcePath = (resource) => {
  const trimmed = (resource || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/**
 * Normalize params to a stable JSON string for dirty comparison.
 * @param {Record<string, string[]|string>|null|undefined} params
 * @returns {string}
 */
export const normalizeParams = (params) => {
  if (!params || typeof params !== 'object') {
    return '{}';
  }

  const normalized = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        acc[key] = [...value].map(String).sort();
      } else if (value === null || value === undefined) {
        acc[key] = [];
      } else {
        acc[key] = [String(value)];
      }
      return acc;
    }, {});

  return JSON.stringify(normalized);
};

/**
 * Compare two param objects for equality.
 * @param {Record<string, string[]|string>|null|undefined} a
 * @param {Record<string, string[]|string>|null|undefined} b
 * @returns {boolean}
 */
export const paramsAreEqual = (a, b) => normalizeParams(a) === normalizeParams(b);

/**
 * Ensure params are in JSR save format: { paramName: ["value1", "value2"] }.
 * @param {Record<string, string[]|string>|null|undefined} params
 * @returns {Record<string, string[]>}
 */
export const toSaveFormat = (params) => {
  if (!params || typeof params !== 'object') {
    return {};
  }

  return Object.keys(params).reduce((acc, key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      acc[key] = value.map(String);
    } else if (value !== null && value !== undefined) {
      acc[key] = [String(value)];
    } else {
      acc[key] = [];
    }
    return acc;
  }, {});
};

/**
 * Parse JSR inputControls/values response into visualize.js params format.
 * @param {object} apiResponse
 * @returns {Record<string, string[]>}
 */
export const parseInputControlValues = (apiResponse) => {
  const states =
    apiResponse?.inputControlState ||
    apiResponse?.inputControlStates ||
    apiResponse?.parameters ||
    [];

  const stateList = Array.isArray(states) ? states : [states];

  return stateList.reduce((acc, state) => {
    if (!state?.id) {
      return acc;
    }

    const paramId = state.id;

    if (Array.isArray(state.options)) {
      const selected = state.options
        .filter((option) => option?.selected === true)
        .map((option) => String(option.value ?? option.label ?? ''));

      if (selected.length > 0) {
        acc[paramId] = selected;
      }
      return acc;
    }

    if (state.value !== undefined && state.value !== null && state.value !== 'NOTHING') {
      acc[paramId] = [String(state.value)];
    }

    return acc;
  }, {});
};

/**
 * @param {import('axios').AxiosInstance} client
 * @param {string} resource
 * @returns {Promise<Array<{ uri: string, id: string, label: string }>>}
 */
export const fetchReportOptionsSummary = async (client, resource) => {
  const path = normalizeResourcePath(resource);
  const { data } = await client.get(`/rest_v2/reports${path}/options`);
  return data?.reportOptionsSummary || [];
};

/**
 * @param {import('axios').AxiosInstance} client
 * @param {string} resource
 * @param {string} label
 * @param {Record<string, string[]|string>} params
 */
export const saveReportOptions = async (client, resource, label, params) => {
  const path = normalizeResourcePath(resource);
  const searchParams = new URLSearchParams({
    label: label.trim(),
    overwrite: 'true',
  });
  const body = toSaveFormat(params);

  await client.post(`/rest_v2/reports${path}/options?${searchParams.toString()}`, body);
};

/**
 * @param {import('axios').AxiosInstance} client
 * @param {string} optionUri
 * @returns {Promise<Record<string, string[]>>}
 */
export const fetchOptionParameterValues = async (client, optionUri) => {
  const path = normalizeResourcePath(optionUri);
  const { data } = await client.get(`/rest_v2/reports${path}/inputControls/values`);
  return parseInputControlValues(data);
};

/**
 * @param {import('axios').AxiosInstance} client
 * @param {string} resource
 * @param {string} optionId
 */
export const deleteReportOption = async (client, resource, optionId) => {
  const path = normalizeResourcePath(resource);
  await client.delete(`/rest_v2/reports${path}/options/${optionId}/`);
};
